import type { Doc } from '../document/types.ts';
/**
 * ST-03 Opener monotony (INV-CH-ST-03, ENGINE-SPEC §3.3, §4; change C-D6-ST03).
 *
 * The seed's unique-opener share over the whole document is a type-token ratio (D6). Here each
 * prose sentence's first word looks back to its previous use inside the SAME SECTION; a
 * Kaplan–Meier recurrence curve (censored at the section start) gives the seed's unique-opener
 * share at its own reference size of 25 sentences: uniq = 1 − (1/25)·Σ_{k<25} R(k).
 * Composite = (1 − uniq)·0.5 + deictic·0.35 + participial·0.6, on the seed ramp.
 *
 * Per-paragraph series: each sentence contributes 0.5·w + 0.35·[deictic] + 0.6·[participial] with
 * w = (n1 − d)/n1 when its opener recurred d < n1 sentences back (the exact expected contribution
 * to the seed statistic of a random n1-sentence text), so ΣK/ΣE is the composite on the display
 * scale and the segmentation and hotspot tests are consistent with the prior μ.
 */
import { loadEnglishLexicons } from '../lang/en/lexicons/index.ts';
import {
	type RecurrenceCurve,
	type RecurrenceScan,
	type SeedEquivalent,
	scanRecurrence,
} from '../segments/recurrence.ts';
import {
	abstainResult,
	derivePrior,
	languageGate,
	overdispersion,
	scoredResult,
} from './framework.ts';
import {
	type ProseSentenceList,
	SMALL,
	fmt,
	greenwoodSe,
	makeEvidence,
	minAtRiskFor,
	proseSentenceList,
	seedEquivalentAbsorbing,
} from './helpers.ts';
import type {
	ChannelDef,
	ChannelImpl,
	ChannelResult,
	EvidenceBuilder,
	Measurement,
	Ramp,
	RunContext,
} from './types.ts';

const RAMP: Ramp = [
	[0.1, 15],
	[0.25, 35],
	[0.4, 55],
	[0.55, 72],
	[0.7, 88],
];
const N0 = 25;
const W_REC = 0.5;
const W_DEI = 0.35;
const W_PAR = 0.6;

export const st03Def: ChannelDef = {
	id: 'ST-03',
	name: 'Opener monotony',
	family: 'CHOREO',
	axis: 'authorship',
	registers: ['prose'],
	direction: 1,
	oneWay: false,
	estimator: 'recurrence',
	features: [
		{ id: 'recurrence', label: 'Opener recurred within the section (25-sentence reference)' },
		{ id: 'deictic', label: 'Deictic opener (the / this / these / it / there)' },
		{ id: 'participial', label: 'Participial ramp ("Leveraging …")' },
	],
	minExposure: 6,
	exposureUnit: 'sentences',
	lang: 'native',
	ramp: RAMP,
	unitScale: 1,
	unitLabel: 'composite opener-monotony index',
	prior: derivePrior(RAMP, 1, 'sentences', 1.5),
	weight: 1.0,
	nd: 1,
	changes: ['C-D6-ST03'],
};

export interface Compiled {
	deictic: Set<string>;
	participial: RegExp;
}
let compiled: Compiled | undefined;
/** The deictic-opener set and the participial-opener regex (also read by the bench's measurement layer). */
export function patterns(): Compiled {
	if (!compiled) {
		const ch = loadEnglishLexicons().channels['ch-st03'];
		const p = ch.patterns.participialOpener;
		compiled = {
			deictic: new Set(ch.lists.deicticOpeners ?? ['the', 'this', 'these', 'it', 'there']),
			participial: new RegExp(p?.source ?? '^[A-Z][a-z]+ing\\b', p?.flags ?? ''),
		};
	}
	return compiled;
}

interface St03Scan {
	list: ProseSentenceList;
	/** Sentences with a first word, compacted. */
	N: number;
	/** List index of compact sentence j. */
	src: Uint32Array;
	scan: RecurrenceScan;
	curve: RecurrenceCurve;
	est: SeedEquivalent;
	/** Effective reference size used for the event weights (the baseline's, when frozen). */
	n1: number;
	isDeictic: Uint8Array;
	isParticipial: Uint8Array;
	wRec: (jj: number) => number;
	k: Float64Array;
	e: Float64Array;
	dei: number;
	par: number;
}

/**
 * The opener scan shared by `run` (document value) and `measure` (bench event scan, frozen n1). Returns null when the
 * document has fewer than `minN` sentences with a first word.
 */
function scanDoc(doc: Doc, frozenN1: number | undefined, minN: number): St03Scan | null {
	const list = proseSentenceList(doc);

	// sentences with a first WORD token, compacted; section starts remapped to the compact index
	const keptBefore = new Uint32Array(list.count + 1);
	for (let i = 0; i < list.count; i++)
		keptBefore[i + 1] = (keptBefore[i] as number) + ((list.firstWord[i] as number) >= 0 ? 1 : 0);
	const N = keptBefore[list.count] as number;
	if (N < minN) return null;

	const pat = patterns();
	const T = doc.tokens;
	const src = new Uint32Array(N); // list index of compact sentence j
	const keys = new Float64Array(N);
	const boundary = new Int32Array(N);
	const isDeictic = new Uint8Array(N);
	const isParticipial = new Uint8Array(N);
	const deicticById = new Map<number, boolean>();
	let j = 0;
	for (let i = 0; i < list.count; i++) {
		const t = list.firstWord[i] as number;
		if (t < 0) continue;
		src[j] = i;
		const id = T.id[t] as number;
		keys[j] = id + 1;
		boundary[j] = keptBefore[list.secStart[i] as number] as number;
		let d = deicticById.get(id);
		if (d === undefined) {
			d = pat.deictic.has(T.fold(id));
			deicticById.set(id, d);
		}
		if (d) isDeictic[j] = 1;
		const cs = doc.sentences.charStart[list.sent[i] as number] as number;
		if (pat.participial.test(doc.text.slice(cs, cs + 64))) isParticipial[j] = 1;
		j++;
	}

	const scan = scanRecurrence(keys, boundary, N0);
	const curve = scan.curve;
	const est = seedEquivalentAbsorbing(curve, N0, minAtRiskFor(N));
	const n1 = frozenN1 ?? est.n1;

	const P = doc.paragraphs.count;
	const k = new Float64Array(P);
	const e = new Float64Array(P);
	let dei = 0;
	let par = 0;
	const wRec = (jj: number): number => {
		const d = scan.dist[jj] as number;
		return d > 0 && d < n1 ? (n1 - d) / n1 : 0;
	};
	for (let jj = 0; jj < N; jj++) {
		const p = list.para[src[jj] as number] as number;
		e[p] = (e[p] as number) + 1;
		let kv = W_REC * wRec(jj);
		if (isDeictic[jj]) {
			kv += W_DEI;
			dei++;
		}
		if (isParticipial[jj]) {
			kv += W_PAR;
			par++;
		}
		if (kv > 0) k[p] = (k[p] as number) + kv;
	}
	return { list, N, src, scan, curve, est, n1, isDeictic, isParticipial, wRec, k, e, dei, par };
}

/** The evidence of an opener scan: the first word of each monotone opener, one span per cause. */
function openerEvidence(doc: Doc, s: St03Scan): EvidenceBuilder {
	const T = doc.tokens;
	const ev = makeEvidence();
	for (let jj = 0; jj < s.N; jj++) {
		const t = s.list.firstWord[s.src[jj] as number] as number;
		const st = T.start[t] as number;
		const en = st + (T.len[t] as number);
		const w = s.wRec(jj);
		if (w > 0) ev.add(st, en, 0, W_REC * w);
		if (s.isDeictic[jj]) ev.add(st, en, 1, W_DEI);
		if (s.isParticipial[jj]) ev.add(st, en, 2, W_PAR);
	}
	return ev;
}

/** Event scan of one (context) document: per-sentence opener weights (BENCH-V2-SPEC §5.1). */
function measure(ctx: RunContext): Measurement {
	const { doc } = ctx;
	const s = scanDoc(doc, ctx.frozen?.channels['ST-03']?.features.referenceSize, 0);
	const P = doc.paragraphs.count;
	if (!s) return { k: new Float64Array(P), e: new Float64Array(P) };
	return { k: s.k, e: s.e, evidence: openerEvidence(doc, s) };
}

function run(ctx: RunContext): ChannelResult {
	const def = st03Def;
	const gate = languageGate(def, ctx);
	if (gate.abstain) return abstainResult(def, gate.abstain);
	const notes: string[] = [];
	if (gate.note) notes.push(gate.note);
	const { doc } = ctx;
	const sc = scanDoc(doc, undefined, def.minExposure);
	if (!sc) {
		const N = proseSentenceList(doc).firstWord.reduce((a, t) => a + (t >= 0 ? 1 : 0), 0);
		return abstainResult(def, `insufficient text: ${N} sentences < ${def.minExposure}`, notes);
	}
	const { N, curve, est, n1, k, e, dei, par } = sc;
	if (est.shortLookback) notes.push('shortLookback');
	const P = doc.paragraphs.count;
	let K = 0;
	let E = 0;
	for (let p = 0; p < P; p++) {
		K += k[p] as number;
		E += e[p] as number;
	}
	const deictic = dei / N;
	const participial = par / N;
	const uniq = 1 - est.value;
	const composite = W_REC * (1 - uniq) + W_DEI * deictic + W_PAR * participial;

	const phi = overdispersion(k, e, K, E, def.prior.phi0);
	const seRec = greenwoodSe(curve, n1) * Math.sqrt(phi);
	const se = Math.sqrt(
		(W_REC * seRec) ** 2 +
			(W_DEI ** 2 * deictic * (1 - deictic)) / N +
			(W_PAR ** 2 * participial * (1 - participial)) / N
	);
	const ci90: [number, number] = [Math.max(0, composite - 1.645 * se), composite + 1.645 * se];
	const kappa = (N / (N + def.prior.m)) * est.kappaFactor;

	// evidence: the first word of each monotone opener, one span per cause (weights sum to the event weight)
	const ev = openerEvidence(doc, sc);

	return scoredResult(def, {
		K,
		E,
		raw: composite,
		estimate: composite,
		ci90,
		deviation: (def.direction * (composite - def.prior.mu)) / Math.max(se, SMALL),
		phi,
		kappa,
		perParagraph: { k, e },
		evidence: ev.build(),
		notes,
		features: {
			uniqueOpeners: uniq,
			deictic,
			participial,
			composite,
			referenceSize: n1,
			sentences: N,
		},
		summary: [
			`unique openers ${fmt(uniq * 100, 0)}% · deictic ${fmt(deictic * 100, 0)}% · participial ${fmt(participial * 100, 0)}%`,
		],
	});
}

export const st03: ChannelImpl = { def: st03Def, phase: 'event', run, measure };
